import React from "react";
import { Data, Scale, present, xValue } from "./util";

type Props = {
  width: number;
  height: number;
  data: Data;
  xScale: Scale;
};

const UndefinedRegions: React.FunctionComponent<Props> = ({
  height,
  data,
  xScale,
}) => {
  const arbitrarySeries = Object.values(data)[0];
  type RegionChunks = {
    chunks: { start: number; end: number }[];
    prevPresent: boolean | undefined;
    lastPresentX: number | undefined;
  };
  const regions = arbitrarySeries.reduce<RegionChunks>(
    (state, curr, idx) => {
      const isFirstPoint = idx === 0;
      const isLastPoint = idx === arbitrarySeries.length - 1;
      const isPresent = present(curr);
      const currX = xValue(curr);
      if (isFirstPoint) {
        return {
          ...state,
          lastPresentX: currX,
          prevPresent: isPresent,
        };
      } else if (
        !state.prevPresent &&
        (isPresent || isLastPoint) &&
        state.lastPresentX !== undefined
      ) {
        // if we still have an open undefined region, we need to close
        // it, regardless of whether the current point is present
        return {
          chunks: state.chunks.concat({
            start: state.lastPresentX,
            end: currX,
          }),
          lastPresentX: currX,
          prevPresent: true,
        };
      } else {
        return {
          ...state,
          prevPresent: isPresent,
          lastPresentX: isPresent ? currX : state.lastPresentX,
        };
      }
    },
    {
      chunks: [],
      lastPresentX: undefined,
      prevPresent: undefined,
    },
  );
  return (
    <>
      <UndefinedPattern id="pattern-slashes" />
      {regions.chunks.map(({ start, end }) => {
        const startX = xScale(start);
        const endX = xScale(end);
        return (
          <rect
            fill="url(#pattern-slashes)"
            key={start}
            opacity="0.5"
            x={startX}
            y={0}
            width={endX - startX}
            height={height}
          />
        );
      })}
    </>
  );
};

export function UndefinedPattern({ id, fill }: { id: string; fill?: string }) {
  const width = 10;
  const height = 10;
  const strokeWidth = 2.5;
  return (
    <pattern
      id={id}
      width={width}
      height={height}
      patternUnits="userSpaceOnUse"
    >
      <rect width={width} height={height} fill={fill ?? "rgb(245, 245, 245)"} />
      <line
        x1={0}
        y1={height / 2}
        x2={width / 2}
        y2={0}
        stroke="rgb(210, 210, 210)"
        strokeLinecap="square"
        strokeWidth={strokeWidth}
      />
      <line
        x1={width / 2}
        y1={height}
        x2={width}
        y2={height / 2}
        stroke="rgb(210, 210, 210)"
        strokeLinecap="square"
        strokeWidth={strokeWidth}
      />
    </pattern>
  );
}

export default UndefinedRegions;
